/**
 * Error pages and components
 * SCR-005: Error Pages (404, 500, maintenance)
 *
 * @requirement BRD-000 Foundational Screens
 * @screen SCR-005 Error Pages
 * @iso27001 A.12.1.1 Documented operating procedures
 */

import { Component, type ReactNode, type ErrorInfo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Icon, Button } from '@myvault/shared/components';

// ============= Error Illustrations =============

function NotFoundIllustration() {
  return (
    <svg
      className="mx-auto h-48 w-48 text-mv-gray-300"
      viewBox="0 0 200 200"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <circle cx="100" cy="100" r="80" stroke="currentColor" strokeWidth="4" />
      <path
        d="M65 80C65 80 75 70 85 80"
        stroke="currentColor"
        strokeWidth="4"
        strokeLinecap="round"
      />
      <path
        d="M115 80C115 80 125 70 135 80"
        stroke="currentColor"
        strokeWidth="4"
        strokeLinecap="round"
      />
      <path
        d="M70 130C70 130 100 110 130 130"
        stroke="currentColor"
        strokeWidth="4"
        strokeLinecap="round"
      />
      <text x="100" y="180" textAnchor="middle" className="fill-mv-gray-400 text-sm">
        ?
      </text>
    </svg>
  );
}

function ServerErrorIllustration() {
  return (
    <svg
      className="mx-auto h-48 w-48 text-mv-gray-300"
      viewBox="0 0 200 200"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <rect x="40" y="40" width="120" height="30" rx="4" stroke="currentColor" strokeWidth="4" />
      <rect x="40" y="85" width="120" height="30" rx="4" stroke="currentColor" strokeWidth="4" />
      <rect x="40" y="130" width="120" height="30" rx="4" stroke="currentColor" strokeWidth="4" />
      <circle cx="55" cy="55" r="6" fill="currentColor" />
      <circle cx="55" cy="100" r="6" fill="currentColor" />
      <circle cx="55" cy="145" r="6" fill="#EF4444" />
      <path d="M150 170L170 190M170 170L150 190" stroke="#EF4444" strokeWidth="6" strokeLinecap="round" />
    </svg>
  );
}

function MaintenanceIllustration() {
  return (
    <svg
      className="mx-auto h-48 w-48 text-mv-amber-500"
      viewBox="0 0 200 200"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <circle cx="100" cy="100" r="70" stroke="currentColor" strokeWidth="4" />
      <path
        d="M100 50V100L130 120"
        stroke="currentColor"
        strokeWidth="6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M140 160L160 180"
        stroke="currentColor"
        strokeWidth="8"
        strokeLinecap="round"
      />
      <circle cx="170" cy="170" r="15" stroke="currentColor" strokeWidth="4" />
      <path d="M165 165L175 175M175 165L165 175" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
    </svg>
  );
}

function OfflineIllustration() {
  return (
    <svg
      className="mx-auto h-48 w-48 text-mv-gray-300"
      viewBox="0 0 200 200"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M100 50C60 50 30 80 30 120"
        stroke="currentColor"
        strokeWidth="4"
        strokeLinecap="round"
      />
      <path
        d="M100 50C140 50 170 80 170 120"
        stroke="currentColor"
        strokeWidth="4"
        strokeLinecap="round"
      />
      <path
        d="M100 80C75 80 55 100 55 120"
        stroke="currentColor"
        strokeWidth="4"
        strokeLinecap="round"
      />
      <path
        d="M100 80C125 80 145 100 145 120"
        stroke="currentColor"
        strokeWidth="4"
        strokeLinecap="round"
      />
      <circle cx="100" cy="140" r="10" fill="currentColor" />
      <path d="M40 40L160 160" stroke="#EF4444" strokeWidth="6" strokeLinecap="round" />
    </svg>
  );
}

// ============= Error Page Components =============

export function NotFoundPage() {
  const navigate = useNavigate();

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-mv-gray-50 p-6">
      <div className="max-w-md text-center">
        <NotFoundIllustration />

        <h1 className="mt-8 text-4xl font-bold text-mv-gray-900">404</h1>
        <h2 className="mt-2 text-xl font-medium text-mv-gray-600">Pagina niet gevonden</h2>

        <p className="mt-4 text-mv-gray-500">
          De pagina die je zoekt bestaat niet of is verplaatst.
          Controleer de URL of ga terug naar het dashboard.
        </p>

        <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:justify-center">
          <Button onClick={() => navigate(-1)} variant="outline">
            <Icon name="arrowLeft" variant="outline" size="sm" className="mr-2" />
            Ga terug
          </Button>
          <Button asChild>
            <Link to="/dashboard">
              <Icon name="home" variant="outline" size="sm" className="mr-2" />
              Naar dashboard
            </Link>
          </Button>
        </div>

        <div className="mt-12 rounded-lg border border-mv-gray-200 bg-white p-4">
          <p className="text-sm text-mv-gray-600">
            Hulp nodig?{' '}
            <Link to="/help" className="font-medium text-mv-blue-600 hover:text-mv-blue-800">
              Bezoek ons helpcentrum
            </Link>{' '}
            of{' '}
            <a
              href="mailto:support@myvault.nl"
              className="font-medium text-mv-blue-600 hover:text-mv-blue-800"
            >
              neem contact op
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}

export function ServerErrorPage({
  error,
  resetErrorBoundary,
}: {
  error?: Error;
  resetErrorBoundary?: () => void;
}) {
  const handleRefresh = () => {
    if (resetErrorBoundary) {
      resetErrorBoundary();
    } else {
      window.location.reload();
    }
  };

  const handleGoHome = () => {
    window.location.href = '/dashboard';
  };

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-mv-gray-50 p-6">
      <div className="max-w-md text-center">
        <ServerErrorIllustration />

        <h1 className="mt-8 text-4xl font-bold text-mv-gray-900">500</h1>
        <h2 className="mt-2 text-xl font-medium text-mv-gray-600">Er ging iets mis</h2>

        <p className="mt-4 text-mv-gray-500">
          Er is een onverwachte fout opgetreden. Ons team is op de hoogte gebracht
          en werkt aan een oplossing.
        </p>

        {error && import.meta.env.DEV && (
          <div className="mt-4 rounded-lg border border-red-200 bg-red-50 p-4 text-left">
            <p className="text-sm font-mono text-red-800">{error.message}</p>
            {error.stack && (
              <pre className="mt-2 text-xs text-red-700 overflow-auto max-h-40">{error.stack}</pre>
            )}
          </div>
        )}

        <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:justify-center">
          <Button onClick={handleRefresh} variant="outline">
            <Icon name="arrowPath" variant="outline" size="sm" className="mr-2" />
            Probeer opnieuw
          </Button>
          <Button onClick={handleGoHome}>
            <Icon name="home" variant="outline" size="sm" className="mr-2" />
            Naar dashboard
          </Button>
        </div>

        <div className="mt-12 rounded-lg border border-mv-gray-200 bg-white p-4">
          <p className="text-sm text-mv-gray-600">
            Blijft het probleem bestaan?{' '}
            <a
              href="mailto:support@myvault.nl"
              className="font-medium text-mv-blue-600 hover:text-mv-blue-800"
            >
              Neem contact op met support
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}

export function MaintenancePage() {
  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-mv-gray-50 p-6">
      <div className="max-w-md text-center">
        <MaintenanceIllustration />

        <h1 className="mt-8 text-2xl font-bold text-mv-gray-900">Onderhoudswerkzaamheden</h1>
        <h2 className="mt-2 text-lg font-medium text-mv-amber-600">
          We zijn zo terug!
        </h2>

        <p className="mt-4 text-mv-gray-500">
          We voeren op dit moment onderhoud uit om myVault nog beter te maken.
          We verwachten binnen enkele minuten weer online te zijn.
        </p>

        <div className="mt-8 rounded-lg border border-mv-amber-200 bg-mv-amber-50 p-4">
          <div className="flex items-center justify-center gap-2 text-mv-amber-800">
            <Icon name="clock" variant="outline" size="sm" />
            <span className="text-sm font-medium">Geschatte tijd: 15 minuten</span>
          </div>
        </div>

        <div className="mt-8">
          <Button onClick={() => window.location.reload()}>
            <Icon name="arrowPath" variant="outline" size="sm" className="mr-2" />
            Controleer status
          </Button>
        </div>

        <div className="mt-12">
          <p className="text-sm text-mv-gray-600">
            Volg updates op{' '}
            <a
              href="https://status.myvault.nl"
              target="_blank"
              rel="noopener noreferrer"
              className="font-medium text-mv-blue-600 hover:text-mv-blue-800"
            >
              status.myvault.nl
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}

export function OfflinePage() {
  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-mv-gray-50 p-6">
      <div className="max-w-md text-center">
        <OfflineIllustration />

        <h1 className="mt-8 text-2xl font-bold text-mv-gray-900">Je bent offline</h1>

        <p className="mt-4 text-mv-gray-500">
          Het lijkt erop dat je geen internetverbinding hebt.
          Controleer je verbinding en probeer het opnieuw.
        </p>

        <div className="mt-8">
          <Button onClick={() => window.location.reload()}>
            <Icon name="arrowPath" variant="outline" size="sm" className="mr-2" />
            Opnieuw proberen
          </Button>
        </div>

        <div className="mt-8 rounded-lg border border-mv-gray-200 bg-white p-4">
          <h3 className="font-medium text-mv-gray-900">Offline beschikbaar</h3>
          <p className="mt-1 text-sm text-mv-gray-600">
            Sommige functies zijn offline beschikbaar wanneer je eerder was ingelogd.
          </p>
        </div>
      </div>
    </div>
  );
}

// ============= Maintenance Banner =============

export function MaintenanceBanner({
  message,
  scheduledAt,
  onDismiss,
}: {
  message?: string;
  scheduledAt?: string;
  onDismiss?: () => void;
}) {
  return (
    <div className="bg-mv-amber-500 px-4 py-2 text-center text-mv-amber-900">
      <div className="flex items-center justify-center gap-2">
        <Icon name="wrenchScrewdriver" variant="outline" size="sm" />
        <span className="text-sm font-medium">
          {message || 'Gepland onderhoud'}{' '}
          {scheduledAt && (
            <span className="text-mv-amber-800">
              op {new Date(scheduledAt).toLocaleDateString('nl-NL', {
                day: 'numeric',
                month: 'long',
                hour: '2-digit',
                minute: '2-digit',
              })}
            </span>
          )}
        </span>
        {onDismiss && (
          <button
            onClick={onDismiss}
            className="ml-2 rounded p-0.5 hover:bg-mv-amber-400"
          >
            <Icon name="xMark" variant="outline" size="sm" />
          </button>
        )}
      </div>
    </div>
  );
}

// ============= Offline Indicator =============

export function OfflineIndicator() {
  return (
    <div className="fixed bottom-4 left-4 z-50 rounded-lg bg-mv-gray-900 px-4 py-2 text-white shadow-lg">
      <div className="flex items-center gap-2">
        <div className="h-2 w-2 animate-pulse rounded-full bg-red-500" />
        <span className="text-sm font-medium">Offline</span>
      </div>
    </div>
  );
}

// ============= Error Boundary =============

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);

    // In production, send to error tracking service
    if (import.meta.env.PROD) {
      // TODO: Send to error tracking service (Sentry, etc.)
    }
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      return (
        this.props.fallback || (
          <ServerErrorPage
            error={this.state.error || undefined}
            resetErrorBoundary={this.handleReset}
          />
        )
      );
    }

    return this.props.children;
  }
}

// ============= Exports =============

export default {
  NotFoundPage,
  ServerErrorPage,
  MaintenancePage,
  OfflinePage,
  MaintenanceBanner,
  OfflineIndicator,
  ErrorBoundary,
};
